import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { TermsAndConditionsModal } from './TermsAgreement';
import { installClickAnalytics, trackPageView } from '../utils/siteAnalytics';

const Landing = lazy(() => import('./Landing'));
const Company = lazy(() => import('./Company'));
const HomePage = lazy(() => import('./HomePage'));
const EmployerDashboard = lazy(() => import('./EmployerDashboard'));
const JobSeeker = lazy(() => import('./JobSeeker'));
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage'));
const AdminPanel = lazy(() => import('./AdminPanel'));
const SocialAuthComplete = lazy(() => import('./SocialAuthComplete'));
const TutorialPage = lazy(() => import('./TutorialPage'));

const APP_MODE_STORAGE_KEY = 'jumptakeAppMode';
const PUBLIC_HOME_THEME_KEY = 'jumptakePublicHomeTheme';
const COOKIE_CONSENT_STORAGE_KEY = 'jumptakeCookieConsent';
const ADMIN_PANEL_PATH = process.env.REACT_APP_ADMIN_PANEL_PATH || '/jt-owner-console-9x4k2-admin';
const PORTAL_THEME_PATHS = ['/home', '/employer-dashboard'];
const PUBLIC_THEME_PATHS = ['/', '/job-seeker', '/company', '/reset-password', '/social-auth-complete', '/tutorials'];

const getInitialAppMode = () => {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  try {
    const isPublicHome = window.location.pathname === '/';
    const previewTheme = new URLSearchParams(window.location.search).get('theme');
    const publicHomeMode = localStorage.getItem(PUBLIC_HOME_THEME_KEY);

    if (isPublicHome) {
      if (previewTheme === 'dark' || previewTheme === 'light') {
        return previewTheme;
      }

      return publicHomeMode === 'dark' || publicHomeMode === 'light'
        ? publicHomeMode
        : 'dark';
    }

    const savedMode = localStorage.getItem(APP_MODE_STORAGE_KEY);
    return savedMode === 'dark' ? 'dark' : 'light';
  } catch (error) {
    return window.location.pathname === '/' ? 'dark' : 'light';
  }
};

const getInitialCookieConsent = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const CookieStartPage = ({ onChoice }) => (
  <main className="cookie-start-page" aria-label="Cookie notice">
    <section className="cookie-start-card">
      <svg className="cookie-start-svg" x="0px" y="0px" viewBox="0 0 122.88 122.25" xmlSpace="preserve" aria-hidden="true">
        <g>
          <path d="M101.77,49.38c2.09,3.1,4.37,5.11,6.86,5.78c2.45,0.66,5.32,0.06,8.7-2.01c1.36-0.84,3.14-0.41,3.97,0.95 c0.28,0.46,0.42,0.96,0.43,1.47c0.13,1.4,0.21,2.82,0.24,4.26c0.03,1.46,0.02,2.91-0.05,4.35h0v0c0,0.13-0.01,0.26-0.03,0.38 c-0.91,16.72-8.47,31.51-20,41.93c-11.55,10.44-27.06,16.49-43.82,15.69v0.01h0c-0.13,0-0.26-0.01-0.38-0.03 c-16.72-0.91-31.51-8.47-41.93-20C5.31,90.61-0.73,75.1,0.07,58.34H0.07v0c0-0.13,0.01-0.26,0.03-0.38 C1,41.22,8.81,26.35,20.57,15.87C32.34,5.37,48.09-0.73,64.85,0.07V0.07h0c1.6,0,2.89,1.29,2.89,2.89c0,0.4-0.08,0.78-0.23,1.12 c-1.17,3.81-1.25,7.34-0.27,10.14c0.89,2.54,2.7,4.51,5.41,5.52c1.44,0.54,2.2,2.1,1.74,3.55l0.01,0 c-1.83,5.89-1.87,11.08-0.52,15.26c0.82,2.53,2.14,4.69,3.88,6.4c1.74,1.72,3.9,3,6.39,3.78c4.04,1.26,8.94,1.18,14.31-0.55 C99.73,47.78,101.08,48.3,101.77,49.38L101.77,49.38z M59.28,57.86c2.77,0,5.01,2.24,5.01,5.01c0,2.77-2.24,5.01-5.01,5.01 c-2.77,0-5.01-2.24-5.01-5.01C54.27,60.1,56.52,57.86,59.28,57.86L59.28,57.86z M37.56,78.49c3.37,0,6.11,2.73,6.11,6.11 s-2.73,6.11-6.11,6.11s-6.11-2.73-6.11-6.11S34.18,78.49,37.56,78.49L37.56,78.49z M50.72,31.75c2.65,0,4.79,2.14,4.79,4.79 c0,2.65-2.14,4.79-4.79,4.79c-2.65,0-4.79-2.14-4.79-4.79C45.93,33.89,48.08,31.75,50.72,31.75L50.72,31.75z M119.3,32.4 c1.98,0,3.58,1.6,3.58,3.58c0,1.98-1.6,3.58-3.58,3.58s-3.58-1.6-3.58-3.58C115.71,34.01,117.32,32.4,119.3,32.4L119.3,32.4z M93.62,22.91c2.98,0,5.39,2.41,5.39,5.39c0,2.98-2.41,5.39-5.39,5.39c-2.98,0-5.39-2.41-5.39-5.39 C88.23,25.33,90.64,22.91,93.62,22.91L93.62,22.91z M97.79,0.59c3.19,0,5.78,2.59,5.78,5.78c0,3.19-2.59,5.78-5.78,5.78 c-3.19,0-5.78-2.59-5.78-5.78C92.02,3.17,94.6,0.59,97.79,0.59L97.79,0.59z M76.73,80.63c4.43,0,8.03,3.59,8.03,8.03 c0,4.43-3.59,8.03-8.03,8.03s-8.03-3.59-8.03-8.03C68.7,84.22,72.29,80.63,76.73,80.63L76.73,80.63z M31.91,46.78 c4.8,0,8.69,3.89,8.69,8.69c0,4.8-3.89,8.69-8.69,8.69s-8.69-3.89-8.69-8.69C23.22,50.68,27.11,46.78,31.91,46.78L31.91,46.78z M107.13,60.74c-3.39-0.91-6.35-3.14-8.95-6.48c-5.78,1.52-11.16,1.41-15.76-0.02c-3.37-1.05-6.32-2.81-8.71-5.18 c-2.39-2.37-4.21-5.32-5.32-8.75c-1.51-4.66-1.69-10.2-0.18-16.32c-3.1-1.8-5.25-4.53-6.42-7.88c-1.06-3.05-1.28-6.59-0.61-10.35 C47.27,5.95,34.3,11.36,24.41,20.18C13.74,29.69,6.66,43.15,5.84,58.29l0,0.05v0h0l-0.01,0.13v0C5.07,73.72,10.55,87.82,20.02,98.3 c9.44,10.44,22.84,17.29,38,18.1l0.05,0h0v0l0.13,0.01h0c15.24,0.77,29.35-4.71,39.83-14.19c10.44-9.44,17.29-22.84,18.1-38l0-0.05 v0h0l0.01-0.13v0c0.07-1.34,0.09-2.64,0.06-3.91C112.98,61.34,109.96,61.51,107.13,60.74L107.13,60.74z M116.15,64.04L116.15,64.04 L116.15,64.04L116.15,64.04z M58.21,116.42L58.21,116.42L58.21,116.42L58.21,116.42z" />
        </g>
      </svg>
      <p className="cookie-start-heading">We use cookies.</p>
      <p className="cookie-start-description">
        This website uses cookies to ensure you get the best experience on our site.
      </p>
      <div className="cookie-start-actions">
        <button type="button" className="cookie-start-accept" onClick={() => onChoice('allowed')}>
          Allow
        </button>
        <button type="button" className="cookie-start-decline" onClick={() => onChoice('declined')}>
          Decline
        </button>
      </div>
    </section>
  </main>
);

const RouteLoadingFallback = () => (
  <main className="cookie-start-page" aria-label="Loading">
    <section className="cookie-start-card">
      <p className="cookie-start-heading">Loading</p>
    </section>
  </main>
);

const SiteAnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => installClickAnalytics(), []);

  return null;
};

const AppRoutes = ({ appMode, setAppMode }) => {
  const location = useLocation();

  useEffect(() => {
    const activeMode = appMode === 'dark' ? 'dark' : 'light';
    if (appMode !== activeMode) {
      setAppMode(activeMode);
    }

    try {
      localStorage.setItem(APP_MODE_STORAGE_KEY, activeMode);
      localStorage.setItem(PUBLIC_HOME_THEME_KEY, activeMode);
    } catch (error) {
      // The selected mode still applies for this session when storage is unavailable.
    }

    const isPortalRoute = PORTAL_THEME_PATHS.some((path) => location.pathname.startsWith(path))
      || location.pathname.startsWith(ADMIN_PANEL_PATH);
    document.body.removeAttribute('data-jumptake-desktop-scale');

    if (isPortalRoute) {
      document.documentElement.setAttribute('data-theme', activeMode);
      document.body.setAttribute('data-theme', activeMode);
      document.documentElement.removeAttribute('data-public-home-theme');
      document.body.removeAttribute('data-public-home-theme');
      return;
    }

    document.documentElement.removeAttribute('data-theme');
    document.body.removeAttribute('data-theme');
    if (PUBLIC_THEME_PATHS.includes(location.pathname)) {
      document.documentElement.setAttribute('data-public-home-theme', activeMode);
      document.body.setAttribute('data-public-home-theme', activeMode);
    } else {
      document.documentElement.removeAttribute('data-public-home-theme');
      document.body.removeAttribute('data-public-home-theme');
    }
  }, [appMode, location.pathname, setAppMode]);

  return (
    <div className="app-container">
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Landing onThemeChange={setAppMode} />} />
          <Route path="/job-seeker" element={<JobSeeker />} />
          <Route path="/company" element={<Company />} />
          <Route path="/home" element={<HomePage appMode={appMode} onAppModeChange={setAppMode} />} />
          <Route path="/employer-dashboard" element={<EmployerDashboard appMode={appMode} onAppModeChange={setAppMode} />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/social-auth-complete" element={<SocialAuthComplete />} />
          <Route path="/tutorials" element={<TutorialPage theme={appMode} onThemeChange={setAppMode} />} />
          <Route
            path={ADMIN_PANEL_PATH}
            element={<AdminPanel appMode={appMode} onAppModeChange={setAppMode} />}
          />
        </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  const [appMode, setAppMode] = useState(getInitialAppMode);
  const [cookieConsent, setCookieConsent] = useState(getInitialCookieConsent);
  const [contentSafetyWarning, setContentSafetyWarning] = useState('');

  useEffect(() => {
    const showContentSafetyWarning = (event) => {
      setContentSafetyWarning(event.detail?.message || 'Please keep your content professional and review the Terms and Conditions.');
    };
    window.addEventListener('jumptake-content-warning', showContentSafetyWarning);
    return () => window.removeEventListener('jumptake-content-warning', showContentSafetyWarning);
  }, []);

  const handleCookieChoice = (choice) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, choice);
    } catch (error) {
      // Continue into the app even when storage is unavailable.
    }
    setCookieConsent(choice);
  };

  if (!cookieConsent) {
    return <CookieStartPage onChoice={handleCookieChoice} />;
  }

  return (
    <BrowserRouter>
      <SiteAnalyticsTracker />
      <AppRoutes appMode={appMode} setAppMode={setAppMode} />
      <TermsAndConditionsModal
        open={Boolean(contentSafetyWarning)}
        warningMessage={contentSafetyWarning}
        onClose={() => setContentSafetyWarning('')}
      />
    </BrowserRouter>
  );
}

export default App;
